import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { istDay, type Labels, type RepriceInput, type RevertInput } from "./priceList";

/**
 * Price-list documents (owner 2026-10-29): every rate card uploaded and every
 * price list read, saved, applied or downloaded, kept in the database so the
 * original is never lost and any version can be reverted to. radaccess
 * /access/pricelistdocs/{ns}; the operator's Owner and Admin, and superadmin
 * (owner D2). A document is never edited: a change is a new document naming
 * the one it came from.
 */
export type DocKind = "original" | "proposal" | "saved" | "applied" | "snapshot";

export interface PriceListDoc {
  id: number;
  namespace: string;
  kind: DocKind;
  title: string;
  filename: string;
  mediaType: string;
  sizeBytes: number;
  sha256: string;
  parentId?: number;
  note?: string;
  /** applied: the India date its prices start. */
  effectiveFrom?: string;
  createdBy: string;
  createdAt: string;
}

export interface PriceListDocWithContent extends PriceListDoc {
  contentBase64: string;
}

export interface SaveDocInput {
  kind: DocKind;
  title: string;
  filename: string;
  mediaType: string;
  contentBase64: string;
  parentId?: number;
  note?: string;
  /** applied only: the India date its prices start. */
  effectiveFrom?: string;
}

/** Owner D1: 10 MB a document. */
export const MAX_DOC_BYTES = 10 * 1024 * 1024;

export const KIND_WORDS: Record<DocKind, string> = {
  original: "Original document",
  proposal: "Read from a document",
  saved: "Saved",
  applied: "Applied to billing",
  snapshot: "Downloaded from billing",
};

export const MARKDOWN = "text/markdown";

/** Owner D2: the operator's Owner and Admin, and superadmin -- never a staff role. */
export function canKeepPriceListDocs(roles: string[]): boolean {
  return roles.some((r) => ["superadmin", "operator", "manager"].includes(r.toLowerCase()));
}

const enc = encodeURIComponent;

export async function listPriceListDocs(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<PriceListDoc[]> {
  const r = await callGateway<PriceListDoc[] | null>(identity, config().gatewayBase, `/pricelistdocs/${enc(ns)}`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function getPriceListDoc(identity: CallerIdentity, ns: string, id: number): Promise<PriceListDocWithContent> {
  const r = await callGateway<PriceListDocWithContent>(identity, config().gatewayBase, `/pricelistdocs/${enc(ns)}/${id}`);
  return r.data;
}

export async function savePriceListDoc(identity: CallerIdentity, ns: string, input: SaveDocInput): Promise<PriceListDoc> {
  const r = await callGateway<PriceListDoc>(identity, config().gatewayBase, `/pricelistdocs/${enc(ns)}`, { method: "POST", body: input });
  return r.data;
}

/** UTF-8 text -> base64, safe for ₹ and every other non-ASCII character. */
export function textToBase64(text: string): string {
  return bytesToBase64(new TextEncoder().encode(text));
}

export function bytesToBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export function base64ToText(b64: string): string {
  return new TextDecoder().decode(base64ToBytes(b64));
}

/** A document the console can open as text and edit: the price-list file itself. */
export function isEditable(d: Pick<PriceListDoc, "mediaType">): boolean {
  return d.mediaType === MARKDOWN || d.mediaType === "text/plain";
}

/** Saves bytes as a file in the browser. */
export function saveBytes(bytes: Uint8Array<ArrayBuffer> | string, filename: string, mediaType: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([bytes], { type: mediaType }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** The newest kept document a version came from, as "Original document #12". */
export function lineage(d: PriceListDoc, all: PriceListDoc[]): string {
  const out: string[] = [];
  let at: PriceListDoc | undefined = d;
  const seen = new Set<number>();
  while (at?.parentId !== undefined && !seen.has(at.parentId)) {
    seen.add(at.parentId);
    const parentId: number = at.parentId;
    at = all.find((x) => x.id === parentId);
    out.push(at ? `${KIND_WORDS[at.kind]} #${at.id}` : `#${parentId}`);
  }
  return out.join(" ← ");
}

/**
 * Revert to a previous list (owner 2026-10-29): a kept price list against
 * billing, one line per price (radaccess GET .../{ID}/compare -> radagent).
 * Applying uses only Change price and Revert, line by line; each is
 * idempotent in radbilling, so a run that stops half way is simply run again.
 */
export interface CompareLine {
  labelselector: Labels;
  parentlabelselector?: Labels | null;
  document?: { pricingmode: string; amount: number };
  billing?: { ruleid: string; pricingmode: string; amount: number; effectivefrom: string; effectiveto?: string | null; catalogref?: string };
  action: "same" | "revert" | "change" | "none";
  ruleid?: string;
  reason?: string;
}

export interface CompareResult {
  lines: CompareLine[];
  error?: string;
  at?: string;
}

export async function comparePriceListDoc(identity: CallerIdentity, ns: string, id: number, signal?: AbortSignal): Promise<CompareResult> {
  const r = await callGateway<CompareResult>(identity, config().gatewayBase, `/pricelistdocs/${enc(ns)}/${id}/compare`, signal ? { signal } : {});
  return { lines: r.data?.lines ?? [], ...(r.data?.error ? { error: r.data.error } : {}), ...(r.data?.at ? { at: r.data.at } : {}) };
}

export type ApplyStep =
  | { kind: "change"; line: CompareLine; input: RepriceInput }
  | { kind: "revert"; line: CompareLine; input: RevertInput };

/**
 * The calls that bring billing back to the list from `from` (India). A change
 * starts from tomorrow at the earliest; a revert undoes a started change from
 * `from` or from the change's own start, whichever is later (owner: a past
 * date is allowed, never before the change began).
 */
export function applySteps(lines: CompareLine[], from: string, now: Date): { steps: ApplyStep[]; problems: string[] } {
  const steps: ApplyStep[] = [];
  const problems: string[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return { steps, problems: ["Choose the date the list applies from"] };
  for (const l of lines) {
    if (l.action === "change" && l.ruleid && l.document) {
      if (from < istDay(now, 1)) {
        problems.push("A changed price starts tomorrow at the earliest: choose a later date, or revert only");
        continue;
      }
      steps.push({ kind: "change", line: l, input: { ruleid: l.ruleid, amount: l.document.amount, effectivefrom: from } });
    } else if (l.action === "revert" && l.ruleid && l.billing) {
      const start = istDay(new Date(l.billing.effectivefrom));
      steps.push({ kind: "revert", line: l, input: { ruleid: l.ruleid, effectivefrom: from > start ? from : start } });
    }
  }
  return { steps, problems: [...new Set(problems)] };
}

const lineKey = (l: Pick<CompareLine, "labelselector" | "parentlabelselector">) =>
  JSON.stringify([l.labelselector, l.parentlabelselector ?? null]);

/** Billing has not moved since the person compared: the same action on the same rule to the same price. */
export function stillAsCompared(planned: CompareLine, fresh: CompareLine[]): boolean {
  const f = fresh.find((x) => lineKey(x) === lineKey(planned));
  return Boolean(f && f.action === planned.action && f.ruleid === planned.ruleid && f.document?.amount === planned.document?.amount);
}
